// Cutting a render of a whole timeline down to what a seed stores.
//
// Audiotool renders a project to a fixed length (204 s observed), padding with
// silence; a per-track render covers the whole timeline too. A seed stores the
// arrangement's span — plus whatever rings on past its last beat (a reverb or
// delay tail is part of how the song ends), up to a limit — and each clip's
// own window of its track's render.
//
// Pure: decoded PCM in, 24-bit WAV out, same code in Node and a tab.

import { encodeWav24, type DecodedAudio } from "./master-mix.js";

/** Below this (≈ −80 dBFS) a tail counts as finished. */
const SILENCE = 1e-4;

/**
 * Where the audio after `fromSec` falls silent, looking at most `maxTailSec`
 * further; `fromSec` itself when it is already silent there.
 */
export function tailEnd(a: DecodedAudio, fromSec: number, maxTailSec: number): number {
  const from = Math.min(a.frames, Math.max(0, Math.round(fromSec * a.sampleRate)));
  const limit = Math.min(a.frames, from + Math.round(maxTailSec * a.sampleRate));
  let last = from;
  for (let f = from; f < limit; f++) {
    for (let c = 0; c < a.channels; c++) {
      if (Math.abs(a.samples[f * a.channels + c]!) > SILENCE) {
        last = f + 1;
        break;
      }
    }
  }
  return last / a.sampleRate;
}

/** Frames [fromSec, toSec) as a 24-bit WAV; clamped to the audio. Null when empty. */
export function cutSeconds(a: DecodedAudio, fromSec: number, toSec: number): Uint8Array | null {
  const from = Math.max(0, Math.round(fromSec * a.sampleRate));
  const to = Math.min(a.frames, Math.round(toSec * a.sampleRate));
  if (to <= from) return null;
  return encodeWav24(a.samples.subarray(from * a.channels, to * a.channels), a.channels, a.sampleRate);
}

/** Whether there is any sound at all in [fromSec, toSec). */
export function isAudible(a: DecodedAudio, fromSec: number, toSec: number): boolean {
  const from = Math.max(0, Math.round(fromSec * a.sampleRate)) * a.channels;
  const to = Math.min(a.frames, Math.round(toSec * a.sampleRate)) * a.channels;
  for (let i = from; i < to; i++) if (Math.abs(a.samples[i]!) > SILENCE) return true;
  return false;
}
